import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import {
  Loader2,
  LogOut,
  Download,
  Lock,
  RefreshCw,
  Users,
} from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE_KEY = "atlas_admin_basic";

function buildAuthHeader(user, pass) {
  return "Basic " + btoa(`${user}:${pass}`);
}

export default function AdminLeads() {
  const [auth, setAuth] = useState(() => {
    try {
      return sessionStorage.getItem(STORAGE_KEY) || null;
    } catch {
      return null;
    }
  });
  const [form, setForm] = useState({ username: "", password: "" });
  const [loggingIn, setLoggingIn] = useState(false);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchLeads = useCallback(
    async (header) => {
      const authHeader = header || auth;
      if (!authHeader) return;
      setLoading(true);
      setError(null);
      try {
        const res = await axios.get(`${API}/admin/leads`, {
          headers: { Authorization: authHeader },
        });
        setLeads(res.data || []);
      } catch (err) {
        if (err?.response?.status === 401) {
          setError("Credenciales inválidas.");
          try {
            sessionStorage.removeItem(STORAGE_KEY);
          } catch {
            // ignore storage errors
          }
          setAuth(null);
        } else {
          setError("No se pudieron cargar los leads.");
        }
      } finally {
        setLoading(false);
      }
    },
    [auth],
  );

  useEffect(() => {
    if (auth) fetchLeads(auth);
  }, [auth, fetchLeads]);

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!form.username.trim() || !form.password) return;
    setLoggingIn(true);
    const header = buildAuthHeader(form.username.trim(), form.password);
    try {
      await axios.get(`${API}/admin/check`, {
        headers: { Authorization: header },
      });
      try {
        sessionStorage.setItem(STORAGE_KEY, header);
      } catch {
        // ignore storage errors
      }
      setAuth(header);
      toast.success("Bienvenido al panel ATLAS");
    } catch (err) {
      if (err?.response?.status === 401) {
        toast.error("Credenciales inválidas");
      } else {
        toast.error("No se pudo conectar con el servidor");
      }
    } finally {
      setLoggingIn(false);
    }
  };

  const handleLogout = () => {
    try {
      sessionStorage.removeItem(STORAGE_KEY);
    } catch {
      // ignore storage errors
    }
    setAuth(null);
    setForm({ username: "", password: "" });
    setLeads([]);
  };

  const handleDownloadCsv = async () => {
    if (!auth) return;
    try {
      const res = await axios.get(`${API}/admin/leads.csv`, {
        headers: { Authorization: auth },
        responseType: "blob",
      });
      const blob = new Blob([res.data], { type: "text/csv;charset=utf-8;" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const ts = new Date().toISOString().replace(/[:.]/g, "-");
      a.download = `atlas-leads-${ts}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("CSV descargado");
    } catch (err) {
      toast.error("No se pudo descargar el CSV");
    }
  };

  if (!auth) {
    return <LoginCard form={form} setForm={setForm} onSubmit={handleLogin} loading={loggingIn} />;
  }

  return (
    <div
      data-testid="admin-leads-dashboard"
      className="min-h-screen bg-slate-50 text-slate-900"
    >
      <header className="border-b border-slate-200 bg-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <img
              src="/atlas-logo.png"
              alt="ATLAS Industrial"
              className="h-10 w-auto object-contain select-none"
              draggable={false}
            />
            <div className="pl-3 border-l border-slate-200">
              <div className="font-display text-sm font-bold uppercase tracking-[0.18em] text-slate-500 leading-none">
                Admin
              </div>
              <div className="text-xs text-slate-400 mt-1">Panel de Leads</div>
            </div>
          </div>
          <nav className="hidden sm:flex items-center gap-1">
            <button
              data-testid="admin-nav-leads"
              className="inline-flex items-center gap-1.5 px-3 h-9 rounded-md text-sm font-semibold bg-[#6B46C1]/10 text-[#6B46C1]"
            >
              Leads
            </button>
            <button
              data-testid="admin-nav-metrics"
              onClick={() => (window.location.href = "/admin/metrics")}
              className="inline-flex items-center gap-1.5 px-3 h-9 rounded-md text-sm font-semibold text-slate-600 hover:text-slate-900 hover:bg-slate-100"
            >
              Métricas
            </button>
          </nav>
          <div className="flex items-center gap-2">
            <Button
              data-testid="admin-refresh-btn"
              variant="outline"
              size="sm"
              onClick={() => fetchLeads()}
              disabled={loading}
            >
              <RefreshCw className={`mr-1.5 h-4 w-4 ${loading ? "animate-spin" : ""}`} />
              Actualizar
            </Button>
            <Button
              data-testid="admin-csv-btn"
              size="sm"
              onClick={handleDownloadCsv}
              className="bg-[#6B46C1] hover:bg-[#553C9A] text-white"
            >
              <Download className="mr-1.5 h-4 w-4" />
              Descargar CSV
            </Button>
            <Button
              data-testid="admin-logout-btn"
              variant="ghost"
              size="sm"
              onClick={handleLogout}
            >
              <LogOut className="mr-1.5 h-4 w-4" />
              Salir
            </Button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="font-display text-2xl font-extrabold">
              Solicitudes de Demo
            </h1>
            <p className="text-sm text-slate-500">
              Todas las solicitudes enviadas desde la landing page.
            </p>
          </div>
          <Badge
            variant="secondary"
            data-testid="admin-leads-count"
            className="text-sm px-3 py-1"
          >
            <Users className="mr-1.5 h-3.5 w-3.5" />
            {leads.length} leads
          </Badge>
        </div>

        {error && (
          <div
            className="mb-4 rounded-md border border-red-200 bg-red-50 text-red-700 px-4 py-2 text-sm"
            data-testid="admin-error"
          >
            {error}
          </div>
        )}

        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm">
          <Table data-testid="admin-leads-table">
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead>Fecha</TableHead>
                <TableHead>Nombre</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Empresa</TableHead>
                <TableHead>Teléfono</TableHead>
                <TableHead>Provincia</TableHead>
                <TableHead>Idioma</TableHead>
                <TableHead>UTM</TableHead>
                <TableHead>Mensaje</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && leads.length === 0 && (
                <TableRow>
                  <TableCell colSpan={9} className="text-center py-10 text-slate-500">
                    <Loader2 className="inline h-5 w-5 animate-spin mr-2" />
                    Cargando leads...
                  </TableCell>
                </TableRow>
              )}
              {!loading && leads.length === 0 && (
                <TableRow>
                  <TableCell colSpan={9} className="text-center py-10 text-slate-500">
                    Todavía no hay solicitudes.
                  </TableCell>
                </TableRow>
              )}
              {leads.map((lead) => (
                <TableRow key={lead.id} data-testid={`admin-lead-${lead.id}`}>
                  <TableCell className="text-xs text-slate-500 whitespace-nowrap">
                    {formatDate(lead.created_at)}
                  </TableCell>
                  <TableCell className="font-medium">{lead.nombre}</TableCell>
                  <TableCell className="text-slate-700">{lead.email}</TableCell>
                  <TableCell>{lead.empresa}</TableCell>
                  <TableCell className="whitespace-nowrap">
                    {lead.telefono}
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline">{lead.provincia}</Badge>
                  </TableCell>
                  <TableCell>
                    {lead.language ? (
                      <Badge variant="secondary" className="uppercase">
                        {lead.language}
                      </Badge>
                    ) : (
                      <span className="text-slate-400">—</span>
                    )}
                  </TableCell>
                  <TableCell className="text-xs text-slate-600">
                    {lead.utm_source || lead.utm_medium || lead.utm_campaign ? (
                      <div className="space-y-0.5">
                        {lead.utm_source && <div><span className="text-slate-400">src:</span> {lead.utm_source}</div>}
                        {lead.utm_medium && <div><span className="text-slate-400">med:</span> {lead.utm_medium}</div>}
                        {lead.utm_campaign && <div><span className="text-slate-400">cmp:</span> {lead.utm_campaign}</div>}
                      </div>
                    ) : (
                      <span className="text-slate-400">—</span>
                    )}
                  </TableCell>
                  <TableCell
                    className="max-w-xs truncate text-slate-600"
                    title={lead.mensaje || ""}
                  >
                    {lead.mensaje || "—"}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </main>
    </div>
  );
}

function LoginCard({ form, setForm, onSubmit, loading }) {
  return (
    <div
      data-testid="admin-login-card"
      className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-purple-50 flex items-center justify-center px-4"
    >
      <form
        onSubmit={onSubmit}
        className="w-full max-w-md bg-white rounded-2xl shadow-xl shadow-slate-200/50 border border-slate-100 p-8"
      >
        <div className="flex items-center gap-3 mb-6">
          <div className="h-12 w-12 rounded-xl bg-[#6B46C1]/10 flex items-center justify-center">
            <Lock className="text-[#6B46C1]" size={24} />
          </div>
          <div>
            <h1 className="font-display text-xl font-extrabold">ATLAS · Admin</h1>
            <p className="text-xs text-slate-500">Acceso protegido</p>
          </div>
        </div>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="admin-username">Usuario</Label>
            <Input
              id="admin-username"
              data-testid="admin-username-input"
              value={form.username}
              onChange={(e) => setForm({ ...form, username: e.target.value })}
              placeholder="admin"
              autoComplete="username"
              required
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="admin-password">Contraseña</Label>
            <Input
              id="admin-password"
              data-testid="admin-password-input"
              type="password"
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              placeholder="••••••••"
              autoComplete="current-password"
              required
            />
          </div>
        </div>

        <Button
          type="submit"
          data-testid="admin-login-btn"
          className="mt-6 w-full bg-[#6B46C1] hover:bg-[#553C9A] text-white font-semibold h-11"
          disabled={loading}
        >
          {loading ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Ingresando...
            </>
          ) : (
            "Ingresar"
          )}
        </Button>

        <p className="mt-6 text-center text-xs text-slate-400">
          Panel interno — solo personal autorizado.
        </p>
      </form>
    </div>
  );
}

function formatDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString("es-AR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}
